import { UsersApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as UsersActions from './users.actions';

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions);
  private usersApi = inject(UsersApiService);
  private notification = inject(NotificationService);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      switchMap(({ params }) =>
        this.usersApi.findAll(params).pipe(
          map(response => UsersActions.loadUsersSuccess({
            users: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error) => {
            this.notification.error('admin.notifications.load_users_error');
            return of(UsersActions.loadUsersFailure({ error: error.message || 'Failed to load users' }));
          }),
        ),
      ),
    ),
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.createUser),
      switchMap(({ userData }) =>
        this.usersApi.create(userData).pipe(
          map(user => {
            this.notification.success('admin.notifications.user_created');
            return UsersActions.createUserSuccess({ user });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.create_user_error');
            return of(UsersActions.createUserFailure({ error: error.message || 'Failed to create user' }));
          }),
        ),
      ),
    ),
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUser),
      switchMap(({ id, userData }) =>
        this.usersApi.update(id, userData).pipe(
          map(user => {
            this.notification.success('admin.notifications.user_updated');
            return UsersActions.updateUserSuccess({ user });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.update_user_error');
            return of(UsersActions.updateUserFailure({ error: error.message || 'Failed to update user' }));
          }),
        ),
      ),
    ),
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      switchMap(({ id }) =>
        this.usersApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.user_deleted');
            return UsersActions.deleteUserSuccess({ id });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.delete_user_error');
            return of(UsersActions.deleteUserFailure({ error: error.message || 'Failed to delete user' }));
          }),
        ),
      ),
    ),
  );
}
